import { AbstractMesh, ArcRotateCamera, Scene, Vector3 } from '@babylonjs/core'
import { SceneManager } from './sceneManager'
import {
  getBuildingBoundingBox,
  isDoorMesh,
  isGroundMesh,
  isRoofMesh,
  isSketchMesh,
  isSpanMesh,
  isStoreyMesh,
  isStructuralMesh,
  isWallMesh,
  isWindowMesh,
} from './utils'
import store from '../store'
import { ConstructionPage, ConstructionTab } from '../reducers/construction'
import { BuildingStructuralTypes } from '../models/Structural'
import { SelectionManager } from './selection/selectionManager'
import { HideShowOption } from '../models/Construction'

export interface IBuildingViewMode {
  activate(selectedItemId: string | null): void
  activateBuildingItem(id: string | null): void
}

export class SingleStoreyMode implements IBuildingViewMode {
  private scene?: Scene
  private camera?: ArcRotateCamera

  constructor() {
    this.scene = SceneManager.getInstance().getScene()
    this.camera = SceneManager.getInstance().getCamera()
  }

  activate(selectedItemId: string | null): void {
    const buildingItems =
      this.scene?.meshes.filter((mesh) => isStoreyMesh(mesh)) ?? []

    for (const buildingItem of buildingItems) {
      const boundingVectorsWithoutRoofs =
        buildingItem.getHierarchyBoundingVectors(
          true,
          (mesh) =>
            !isRoofMesh(mesh) && !isSketchMesh(mesh) && !isStructuralMesh(mesh),
        )
      buildingItem.position.y = 0
      buildingItem
        .getChildMeshes(
          false,
          (mesh) => mesh instanceof AbstractMesh && isRoofMesh(mesh),
        )
        .forEach(
          (roof) =>
            (roof.position.y =
              boundingVectorsWithoutRoofs.max.y +
              roof.metadata.relativeToStoreyMaxPositionY +
              100),
        )
    }

    this.activateBuildingItem(selectedItemId)
  }

  activateBuildingItem(id: string | null) {
    if (!id) {
      return
    }

    const itemMesh = this.scene?.getMeshById(id)
    if (itemMesh) {
      setElementVisibility(itemMesh, false)
    }

    const otherItems =
      this.scene?.meshes.filter(
        (mesh) => mesh.id !== id && isStoreyMesh(mesh),
      ) ?? []

    for (const otherItem of otherItems) {
      setElementVisibility(otherItem, true)
    }

    if (this.camera) {
      const boundingVectors = itemMesh?.getHierarchyBoundingVectors() ?? {
        min: Vector3.Zero(),
        max: Vector3.Zero(),
      }
      const cameraTarget = Vector3.Lerp(
        boundingVectors.min,
        boundingVectors.max,
        0.5,
      )

      this.camera.setTarget(cameraTarget, false, false, false)
      this.camera.lowerRadiusLimit = this.camera.radius / 4
      this.camera.upperRadiusLimit = this.camera.radius * 4
    }

    SelectionManager.getInstance().updatePickingList()
  }
}

export class StackableViewMode implements IBuildingViewMode {
  private scene?: Scene
  private camera?: ArcRotateCamera

  constructor() {
    this.scene = SceneManager.getInstance().getScene()
    this.camera = SceneManager.getInstance().getCamera()
  }

  activate(): void {
    const buildingItems =
      this.scene?.meshes.filter((mesh) => isStoreyMesh(mesh)) ?? []

    let minVector = new Vector3(
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
    )
    let maxVector = new Vector3(
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
    )

    let maxHeight = 0
    for (const buildingItem of buildingItems) {
      const boundingVectorsWithoutRoofs =
        buildingItem.getHierarchyBoundingVectors(
          true,
          (mesh) =>
            !isRoofMesh(mesh) && !isSketchMesh(mesh) && !isStructuralMesh(mesh),
        )

      buildingItem
        .getChildMeshes(
          true,
          (child) => child instanceof AbstractMesh && isRoofMesh(child),
        )
        .forEach((roofMesh) => {
          roofMesh.position.y =
            boundingVectorsWithoutRoofs.max.y +
            roofMesh.metadata.relativeToStoreyMaxPositionY +
            100
        })

      const boundingVectorsWithRoofs =
        buildingItem.getHierarchyBoundingVectors()
      const storeyHeightWithRoofs =
        boundingVectorsWithRoofs.max.y - boundingVectorsWithRoofs.min.y
      buildingItem.position.y = maxHeight

      maxHeight += storeyHeightWithRoofs // gap between storeys

      minVector = Vector3.Minimize(minVector, boundingVectorsWithRoofs.min)
      maxVector = Vector3.Maximize(maxVector, boundingVectorsWithRoofs.max)

      setElementVisibility(buildingItem, false)
    }

    if (this.camera) {
      const cameraTarget = Vector3.Lerp(minVector, maxVector, 0.5)
      const maxHeight = maxVector.y - minVector.y

      this.camera.setTarget(cameraTarget, false, false, false)
      this.camera.setPosition(
        new Vector3(
          this.camera.position.x,
          minVector.y +
            (buildingItems[buildingItems.length - 1]?.position.y ?? 0) +
            maxHeight,
          this.camera.position.z,
        ),
      )
      this.camera.lowerRadiusLimit = this.camera.radius / 4
      this.camera.upperRadiusLimit = this.camera.radius * 4
    }

    SelectionManager.getInstance().updatePickingList()
  }

  activateBuildingItem() {}
}

export class WholeBuildingMode implements IBuildingViewMode {
  private scene?: Scene
  private camera?: ArcRotateCamera

  constructor() {
    this.scene = SceneManager.getInstance().getScene()
    this.camera = SceneManager.getInstance().getCamera()
  }

  activate(): void {
    const buildingItems =
      this.scene?.meshes.filter((mesh) => isStoreyMesh(mesh)) ?? []

    for (const buildingItem of buildingItems) {
      const boundingVectorsWithoutRoofs =
        buildingItem.getHierarchyBoundingVectors(
          true,
          (mesh) =>
            !isRoofMesh(mesh) && !isSketchMesh(mesh) && !isStructuralMesh(mesh),
        )

      buildingItem
        .getChildMeshes(
          true,
          (child) => child instanceof AbstractMesh && isRoofMesh(child),
        )
        .forEach((roofMesh) => {
          if (isRoofMesh(roofMesh)) {
            roofMesh.position.y =
              boundingVectorsWithoutRoofs.max.y +
              roofMesh.metadata.relativeToStoreyMaxPositionY
          }
        })

      buildingItem.position.y = 0

      setElementVisibility(buildingItem, false)
    }

    if (this.camera) {
      const buildingBBox = getBuildingBoundingBox()
      if (buildingBBox) {
        const cameraTarget = Vector3.Lerp(
          buildingBBox.min,
          buildingBBox.max,
          0.5,
        )
        const maxHeight = buildingBBox.max.y - buildingBBox.min.y

        this.camera.setTarget(cameraTarget, false, false, false)
        this.camera.setPosition(
          new Vector3(
            this.camera.position.x,
            buildingBBox.min.y +
              (buildingItems[buildingItems.length - 1]?.position.y ?? 0) +
              maxHeight,
            this.camera.position.z,
          ),
        )
        this.camera.lowerRadiusLimit = this.camera.radius / 4
        this.camera.upperRadiusLimit = this.camera.radius * 4
      }
    }

    SelectionManager.getInstance().updatePickingList()
  }

  activateBuildingItem() {}
}

const setElementVisibility = (mesh: AbstractMesh, isHidden: boolean) => {
  if (isStoreyMesh(mesh)) {
    setStoreyVisibility(mesh, isHidden)
  } else if (isWallMesh(mesh)) {
    setWallVisibility(mesh, isHidden)
  } else if (isWindowMesh(mesh)) {
    setWindowVisibility(mesh, isHidden)
  } else if (isDoorMesh(mesh)) {
    setDoorVisibility(mesh, isHidden)
  } else if (isRoofMesh(mesh)) {
    setRoofVisibility(mesh, isHidden)
  } else if (isSpanMesh(mesh)) {
    setSpanVisibility(mesh, isHidden)
  } else if (isGroundMesh(mesh)) {
    setGroundVisibility(mesh, isHidden)
  } else if (isStructuralMesh(mesh)) {
    setStructuralVisibility(mesh, isHidden)
  }
}

const setStoreyVisibility = (storeyMesh: AbstractMesh, isHidden: boolean) => {
  storeyMesh.isVisible = !isHidden
  storeyMesh
    .getChildMeshes(true)
    .forEach((child) => setElementVisibility(child, isHidden))
}

const setWallVisibility = (wallMesh: AbstractMesh, isHidden: boolean) => {
  const isVisible = !(isHidden || Boolean(wallMesh.metadata?.isHidden))
  wallMesh.isVisible = isVisible

  const selectedConstructionPage =
    store.getState().construction.selectedConstructionPage
  wallMesh.isPickable =
    selectedConstructionPage === ConstructionPage.Architecture

  wallMesh.getChildMeshes(true).forEach((child) => {
    if (isSketchMesh(child)) {
      child.isVisible = isVisible
    } else {
      setElementVisibility(child, isHidden)
    }
  })
}

const setWindowVisibility = (windowMesh: AbstractMesh, isHidden: boolean) => {
  const isVisible = !(isHidden || Boolean(windowMesh.metadata?.isHidden))
  windowMesh.isVisible = isVisible

  const selectedConstructionPage =
    store.getState().construction.selectedConstructionPage
  windowMesh.isPickable =
    selectedConstructionPage === ConstructionPage.Architecture

  windowMesh.getChildMeshes().forEach((child) => {
    if (isSketchMesh(child)) {
      child.isVisible = isVisible
    } else {
      setElementVisibility(child, isHidden)
    }
  })
}

const setDoorVisibility = (doorMesh: AbstractMesh, isHidden: boolean) => {
  const isVisible = !(isHidden || Boolean(doorMesh.metadata?.isHidden))
  doorMesh.isVisible = isVisible

  const selectedConstructionPage =
    store.getState().construction.selectedConstructionPage
  doorMesh.isPickable =
    selectedConstructionPage === ConstructionPage.Architecture

  doorMesh.getChildMeshes().forEach((child) => {
    if (isSketchMesh(child)) {
      child.isVisible = isVisible
    } else {
      setElementVisibility(child, isHidden)
    }
  })
}

const setRoofVisibility = (roofMesh: AbstractMesh, isHidden: boolean) => {
  const isRoofsHidden =
    !store.getState().construction.hideShowOptions[
      HideShowOption.RoofsVisibility
    ]
  const isVisible = !(
    isHidden ||
    isRoofsHidden ||
    Boolean(roofMesh.metadata?.isHidden)
  )
  roofMesh.isVisible = isVisible

  const selectedConstructionPage =
    store.getState().construction.selectedConstructionPage
  roofMesh.isPickable =
    selectedConstructionPage === ConstructionPage.Architecture

  roofMesh.getChildMeshes(true).forEach((child) => {
    if (isSketchMesh(child)) {
      child.isVisible = isVisible
    } else {
      setElementVisibility(child, isHidden)
    }
  })
}

const setSpanVisibility = (spanMesh: AbstractMesh, isHidden: boolean) => {
  spanMesh.isVisible = !isHidden
  spanMesh
    .getChildMeshes(true)
    .forEach((child) => setElementVisibility(child, isHidden))
}

const setGroundVisibility = (groundMesh: AbstractMesh, isHidden: boolean) => {
  groundMesh.isVisible = !isHidden
  groundMesh.getChildMeshes(true).forEach((child) => {
    if (isSketchMesh(child)) {
      child.isVisible = !isHidden
    } else {
      setElementVisibility(child, isHidden)
    }
  })
}

const setStructuralVisibility = (
  structualMesh: AbstractMesh,
  isHidden: boolean,
) => {
  const selectedConstructionTab =
    store.getState().construction.selectedConstructionTab
  const selectedConstructionPage =
    store.getState().construction.selectedConstructionPage
  const selectedStructuralTypes =
    store.getState().structural.selectedStructuralTypes
  const structuralType = structualMesh.metadata
    .structuralType as BuildingStructuralTypes

  structualMesh.isVisible =
    !isHidden &&
    selectedConstructionTab === ConstructionTab.Editor &&
    selectedConstructionPage === ConstructionPage.Engineering &&
    selectedStructuralTypes.includes(structuralType)
  structualMesh.isPickable =
    selectedConstructionPage === ConstructionPage.Engineering
}
